import { Handle, Position, type NodeProps } from '@xyflow/react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Brain } from 'lucide-react'
import type { FlowNode } from '@/schemas/workflow-node'
import { NeedsAttentionBadge } from '@/components/workflows/lifecycle/needs-attention-badge'
import { OverrideCanvasBadge } from '@/components/workflows/override-canvas-badge'

export function LLMNode({ data, selected }: NodeProps) {
  const { t } = useTranslation('workflows')
  const nodeData = data as unknown as FlowNode
  const needsAttention =
    (data as unknown as { needs_attention?: boolean })?.needs_attention === true
  return (
    <div
      className={cn(
        'relative rounded-xl border border-border bg-card p-4 min-w-[220px] shadow-sm',
        'border-l-4 border-l-indigo-500',
        needsAttention && 'border-amber-500 border-2',
        selected && 'ring-2 ring-primary'
      )}
    >
      {needsAttention && (
        <div className="absolute top-1 right-1">
          <NeedsAttentionBadge />
        </div>
      )}
      <div className={cn('absolute right-1', needsAttention ? 'top-7' : 'top-1')}>
        <OverrideCanvasBadge config={nodeData.config as Record<string, unknown>} />
      </div>
      <Handle
        type="target"
        position={Position.Top}
        className="!bg-primary !w-3 !h-3"
      />
      <div className="flex items-center gap-1 mb-1">
        <Brain className="w-4 h-4 text-indigo-500" />
        <Badge variant="outline" className="text-xs">
          LLM
        </Badge>
      </div>
      <div className="text-sm font-semibold text-foreground truncate">
        {nodeData.label || 'LLM Node'}
      </div>
      <div className="text-xs text-muted-foreground truncate mt-0.5">
        {(() => {
          const cfg = nodeData.config as
            | { model?: string | { inherited?: boolean; value?: string | null } }
            | undefined
          const m = cfg?.model
          if (typeof m === 'string') return m
          if (m && typeof m === 'object') {
            if (m.inherited) return t('inheritance.inheritedShort')
            return m.value || t('inheritance.noModelSet')
          }
          return t('inheritance.noModelSet')
        })()}
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        className="!bg-primary !w-3 !h-3"
      />
    </div>
  )
}
